<template>
  <div class="data-table-demo">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-teal-500 to-green-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-table" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">📊 DataTable Component Demo</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Tabla de datos avanzada con filtros y acciones</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 🔧 Demo Controls -->
    <v-card class="mb-4" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedRole"
              :items="availableRoles"
              item-title="label"
              item-value="value"
              label="Simular Rol"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-account-switch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="tableVariant"
              :items="tableVariants"
              item-title="label"
              item-value="value"
              label="Variante de Tabla"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="showActions"
              label="Mostrar Acciones"
              color="primary"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-switch
              v-model="enableSelection"
              label="Selección Múltiple"
              color="primary"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 📊 Data Table -->
    <v-card elevation="2">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-table-large" class="me-2" />
        {{ currentTableTitle }}
        <v-spacer />
        <v-btn
          variant="outlined"
          size="small"
          prepend-icon="mdi-refresh"
          @click="refreshData"
        >
          Actualizar
        </v-btn>
      </v-card-title>

      <!-- 🔍 Table Filters -->
      <v-card-text class="pa-4 pb-0" v-if="showTableFilters">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              label="Buscar"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Estado"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="departmentFilter"
              :items="departmentOptions"
              label="Departamento"
              variant="outlined"
              density="compact"
              clearable
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="pa-0">
        <v-data-table
          v-model="selectedItems"
          :headers="currentHeaders"
          :items="filteredItems"
          :loading="loading"
          :show-select="enableSelection"
          item-value="id"
          class="elevation-0"
          :items-per-page="10"
        >
          <!-- Status Chip -->
          <template #item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
              variant="flat"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <!-- Priority Badge -->
          <template #item.priority="{ item }">
            <v-badge
              :color="getPriorityColor(item.priority)"
              :content="item.priority"
              inline
            />
          </template>

          <!-- User Avatar -->
          <template #item.assignedTo="{ item }">
            <div class="d-flex align-center" v-if="item.assignedTo">
              <v-avatar size="24" class="me-2">
                <v-img :src="item.assignedTo.avatar" />
              </v-avatar>
              {{ item.assignedTo.name }}
            </div>
            <span v-else class="text-medium-emphasis">No asignado</span>
          </template>

          <!-- Date Formatting -->
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }" v-if="showActions">
            <div class="d-flex">
              <v-tooltip text="Ver detalles">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-eye"
                    variant="text"
                    size="small"
                    @click="viewItem(item)"
                  />
                </template>
              </v-tooltip>
              
              <v-tooltip text="Editar" v-if="canEdit">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-pencil"
                    variant="text"
                    size="small"
                    color="primary"
                    @click="editItem(item)"
                  />
                </template>
              </v-tooltip>
              
              <v-tooltip text="Eliminar" v-if="canDelete">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-delete"
                    variant="text"
                    size="small"
                    color="error"
                    @click="deleteItem(item)"
                  />
                </template>
              </v-tooltip>

              <v-menu>
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-dots-vertical"
                    variant="text"
                    size="small"
                  />
                </template>
                <v-list>
                  <v-list-item @click="assignItem(item)">
                    <v-list-item-title>Asignar</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="duplicateItem(item)">
                    <v-list-item-title>Duplicar</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="exportItem(item)">
                    <v-list-item-title>Exportar</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>

          <!-- Loading -->
          <template #loading>
            <div class="text-center pa-4">
              <v-progress-circular
                indeterminate
                color="primary"
              />
              <p class="text-caption mt-2">Cargando datos...</p>
            </div>
          </template>

          <!-- No Data -->
          <template #no-data>
            <div class="text-center pa-8">
              <v-icon icon="mdi-database-off" size="48" color="grey" class="mb-2" />
              <p class="text-subtitle-1">No hay datos disponibles</p>
              <v-btn
                color="primary"
                variant="outlined"
                @click="refreshData"
              >
                Actualizar datos
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- 📊 Table Stats -->
      <v-card-actions class="pa-4 bg-surface-variant">
        <div class="d-flex align-center">
          <v-icon icon="mdi-information" size="16" class="me-1" />
          <span class="text-caption">
            {{ filteredItems.length }} elementos
            <span v-if="selectedItems.length > 0">
              • {{ selectedItems.length }} seleccionados
            </span>
          </span>
        </div>
        <v-spacer />
        <v-btn
          v-if="selectedItems.length > 0"
          variant="outlined"
          size="small"
          prepend-icon="mdi-download"
          @click="exportSelected"
        >
          Exportar Seleccionados
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 🔄 Back Navigation -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center/components')"
        >
          Volver a Componentes
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-code-tags"
          @click="showCode"
        >
          Ver Código
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface TableItem {
  id: number
  title: string
  description: string
  status: string
  priority: string
  assignedTo?: {
    name: string
    avatar: string
  }
  department: string
  createdAt: Date
  location: string
}

// 🔧 Reactive state
const store = useMainStore()
const selectedRole = ref('super_admin')
const tableVariant = ref('incidents')
const showActions = ref(true)
const enableSelection = ref(true)
const selectedItems = ref([])
const loading = ref(false)
const searchQuery = ref('')
const statusFilter = ref('')
const departmentFilter = ref('')

// 📊 Mock data
const availableRoles = ref([
  { label: 'Super Admin', value: 'super_admin' },
  { label: 'Gerente Regional', value: 'gerente_regional' },
  { label: 'Supervisor de Zona', value: 'supervisor_zona' },
  { label: 'Operador de Rutas', value: 'operador_rutas' },
  { label: 'Empleado de Agencia', value: 'empleado_agencia' }
])

const tableVariants = ref([
  { label: 'Incidencias', value: 'incidents' },
  { label: 'Usuarios', value: 'users' },
  { label: 'Reportes', value: 'reports' },
  { label: 'Rutas', value: 'routes' }
])

const statusOptions = ref([
  'Activo',
  'Pendiente',
  'Completado',
  'Cancelado'
])

const departmentOptions = ref([
  'Recolección',
  'Operaciones',
  'Mantenimiento',
  'Administración'
])

const mockData = ref<TableItem[]>([
  {
    id: 1,
    title: 'Recolección pendiente en Zona Norte',
    description: 'Camión averiado, requiere sustitución',
    status: 'Pendiente',
    priority: 'Alta',
    assignedTo: {
      name: 'Juan Pérez',
      avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
    },
    department: 'Recolección',
    createdAt: new Date('2024-01-22'),
    location: 'Zona Norte'
  },
  {
    id: 2,
    title: 'Mantenimiento preventivo',
    description: 'Revisión mensual de equipos',
    status: 'Activo',
    priority: 'Media',
    assignedTo: {
      name: 'María García',
      avatar: 'https://randomuser.me/api/portraits/women/2.jpg'
    },
    department: 'Mantenimiento',
    createdAt: new Date('2024-01-21'),
    location: 'Centro'
  },
  {
    id: 3,
    title: 'Reporte semanal completado',
    description: 'Estadísticas de recolección',
    status: 'Completado',
    priority: 'Baja',
    department: 'Operaciones',
    createdAt: new Date('2024-01-20'),
    location: 'Zona Sur'
  },
  {
    id: 4,
    title: 'Incidencia reportada por ciudadano',
    description: 'Contenedor dañado necesita reemplazo',
    status: 'Pendiente',
    priority: 'Crítica',
    assignedTo: {
      name: 'Carlos López',
      avatar: 'https://randomuser.me/api/portraits/men/3.jpg'
    },
    department: 'Operaciones',
    createdAt: new Date('2024-01-22'),
    location: 'Zona Este'
  },
  {
    id: 5,
    title: 'Capacitación de personal',
    description: 'Entrenamiento en nuevos procedimientos',
    status: 'Activo',
    priority: 'Media',
    department: 'Administración',
    createdAt: new Date('2024-01-19'),
    location: 'Oficina Central'
  }
])

// 🎯 Computed properties
const currentTableTitle = computed(() => {
  const variant = tableVariants.value.find(v => v.value === tableVariant.value)
  return `Tabla de ${variant?.label || 'Datos'}`
})

const showTableFilters = computed(() => {
  return ['incidents', 'reports'].includes(tableVariant.value)
})

const currentHeaders = computed(() => {
  const baseHeaders = [
    { title: 'ID', key: 'id', sortable: true, width: 80 },
    { title: 'Título', key: 'title', sortable: true },
    { title: 'Estado', key: 'status', sortable: true, width: 120 },
    { title: 'Departamento', key: 'department', sortable: true, width: 140 }
  ]

  if (tableVariant.value === 'incidents') {
    baseHeaders.splice(3, 0, 
      { title: 'Prioridad', key: 'priority', sortable: true, width: 100 },
      { title: 'Asignado a', key: 'assignedTo', sortable: false, width: 150 }
    )
  }

  baseHeaders.push(
    { title: 'Fecha', key: 'createdAt', sortable: true, width: 120 }
  )

  if (showActions.value) {
    baseHeaders.push(
      { title: 'Acciones', key: 'actions', sortable: false, width: 120 }
    )
  }

  return baseHeaders
})

const filteredItems = computed(() => {
  let filtered = mockData.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query)
    )
  }

  if (statusFilter.value) {
    filtered = filtered.filter(item => item.status === statusFilter.value)
  }

  if (departmentFilter.value) {
    filtered = filtered.filter(item => item.department === departmentFilter.value)
  }

  return filtered
})

const canEdit = computed(() => {
  return ['super_admin', 'gerente_regional', 'supervisor_zona'].includes(selectedRole.value)
})

const canDelete = computed(() => {
  return ['super_admin', 'gerente_regional'].includes(selectedRole.value)
})

// 🎯 Methods
const refreshData = () => {
  loading.value = true
  setTimeout(() => {
    loading.value = false
    store.showNotification({
      type: 'success',
      title: 'Datos Actualizados',
      message: 'La tabla ha sido actualizada correctamente'
    })
  }, 1500)
}

const viewItem = (item: TableItem) => {
  console.log('👁️ Ver item:', item)
  store.showNotification({
    type: 'info',
    title: 'Ver Detalles',
    message: `Abriendo detalles de: ${item.title}`
  })
}

const editItem = (item: TableItem) => {
  console.log('✏️ Editar item:', item)
  store.showNotification({
    type: 'info',
    title: 'Editar',
    message: `Editando: ${item.title}`
  })
}

const deleteItem = (item: TableItem) => {
  console.log('🗑️ Eliminar item:', item)
  store.showNotification({
    type: 'warning',
    title: 'Eliminar',
    message: `¿Confirmar eliminación de: ${item.title}?`
  })
}

const assignItem = (item: TableItem) => {
  console.log('👤 Asignar item:', item)
  store.showNotification({
    type: 'info',
    title: 'Asignar',
    message: `Asignando: ${item.title}`
  })
}

const duplicateItem = (item: TableItem) => {
  console.log('📋 Duplicar item:', item)
  store.showNotification({
    type: 'info',
    title: 'Duplicar',
    message: `Duplicando: ${item.title}`
  })
}

const exportItem = (item: TableItem) => {
  console.log('📤 Exportar item:', item)
  store.showNotification({
    type: 'success',
    title: 'Exportar',
    message: `Exportando: ${item.title}`
  })
}

const exportSelected = () => {
  console.log('📤 Exportar seleccionados:', selectedItems.value)
  store.showNotification({
    type: 'success',
    title: 'Exportación',
    message: `Exportando ${selectedItems.value.length} elementos`
  })
}

const showCode = () => {
  console.log('💻 Mostrar código del componente')
  store.showNotification({
    type: 'info',
    title: 'Código del Componente',
    message: 'Abriendo vista del código fuente...'
  })
}

const formatDate = (date: Date) => {
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    'Activo': 'success',
    'Pendiente': 'warning',
    'Completado': 'info',
    'Cancelado': 'error'
  }
  return colors[status] || 'grey'
}

const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    'Crítica': 'error',
    'Alta': 'warning',
    'Media': 'info',
    'Baja': 'success'
  }
  return colors[priority] || 'grey'
}

// 👀 Watchers
watch(selectedRole, (newRole) => {
  console.log('🔄 Rol cambiado a:', newRole)
  // Simular cambio de permisos
  selectedItems.value = []
})

watch(tableVariant, (newVariant) => {
  console.log('🔄 Variante cambiada a:', newVariant)
  selectedItems.value = []
  searchQuery.value = ''
  statusFilter.value = ''
  departmentFilter.value = ''
})

// 🎬 Lifecycle
onMounted(() => {
  console.log('📊 Demo de DataTable cargado')
})
</script>

<style scoped>
.data-table-demo {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #009688 0%, #4caf50 100%);
}

:deep(.v-data-table__wrapper) {
  border-radius: 0;
}

:deep(.v-data-table-header) {
  background-color: rgba(0, 0, 0, 0.02);
}

:deep(.v-data-table-rows tr:hover) {
  background-color: rgba(25, 118, 210, 0.04);
}
</style>
